// Whole-song analysis for light programs, run once per song in the admin
// page and saved on the hub (SavedAnalysis, read back by shared/song.ts).
//
// One pass of OnsetDetector over the song gives both the onset envelope for
// the beat tracker (beats.ts) and, through its FFT of every 2048-sample frame
// (~86 per second), the features, averaged into 20 frames per second:
//   loudness         K-weighted power, as perceived loudness (doubles every
//                    10 dB) relative to the song's loudest 1%
//   bass/mid/treble  band power, the same scale, each against its own top
//   onset            the beat tracker's onset envelope
//   chroma           spectral peaks from 55 Hz to 2 kHz, located to a
//                    fraction of a bin and folded into 12 note names after
//                    finding the song's tuning
//   register         magnitude-weighted mean log frequency (a log centroid)
// Then per beat: downbeats (the bar phase with the strongest bass, onsets and
// chord changes) and sections (novelty on a self-similarity matrix).

import {
  ANALYSIS_VERSION,
  encodeBase64,
  frameCount,
  MAX_FRAMES,
  NO_PITCH,
  type AnalysisSection,
  type FeatureName,
  type SavedAnalysis,
} from "../shared/library.ts";
import { BEATS_PER_BAR } from "../shared/song.ts";
import { onsetEnvelope, trackBeats } from "./beats.ts";
import { FRAME_SIZE, HOP_SIZE, OnsetDetector } from "./onset.ts";

export const FRAME_RATE = 20;
const BANDS_HZ: [number, number][] = [
  [30, 250],
  [250, 4000],
  [4000, 16000],
];
const CHROMA_HZ: [number, number] = [55, 2000];
const REGISTER_HZ: [number, number] = [60, 8000];
// Chroma is gathered at 5 bins per semitone so the song's tuning (a band a
// little flat, an old recording a little sharp) can be found and removed.
const FINE = 5;
const FINE_BINS = 12 * FINE;
// Below this a note doesn't stand out enough to name (a plain triad is ~0.27).
const MIN_PITCH_CONFIDENCE = 0.2;
// Perceived loudness at -50 dB from the top counts as silence.
const FLOOR = 2 ** -5;
// Anything quieter than this (dB below full scale) is silence whatever the song.
const SILENCE_DB = -70;
const MIN_SECTION_S = 8;
// Novelty (within-similarity minus cross-similarity) needed for a boundary.
const MIN_NOVELTY = 0.2;
// Block similarity at which two sections get the same label.
const SAME_LABEL = 0.75;

export function analyzeAudio(samples: Float32Array, sampleRate: number): SavedAnalysis | null {
  const duration = Math.round((samples.length / sampleRate) * 1000) / 1000;
  if (duration > 4 * 3600) return null;
  let frameRate = FRAME_RATE;
  while (frameRate > 1 && frameCount(duration, frameRate) > MAX_FRAMES) frameRate--;
  const n = frameCount(duration, frameRate);

  const spectra = new SpectrumFeatures(sampleRate, frameRate, n);
  const detector = new OnsetDetector(sampleRate, 1, (re, im, time) => spectra.add(re, im, time));
  const onsetFrames = detector.push(samples, 0);
  const tracked = trackBeats(onsetFrames, sampleRate, duration);
  if (!tracked || tracked.beats.length < 2) return null;

  // Onset envelope at 20 fps: the strongest rise within each frame.
  const env = onsetEnvelope(
    onsetFrames.map((f) => f.flux),
    sampleRate / HOP_SIZE,
  );
  const onsetRaw = new Float32Array(n);
  onsetFrames.forEach((f, m) => {
    const j = Math.min(n - 1, Math.round(f.time * frameRate));
    onsetRaw[j] = Math.max(onsetRaw[j]!, env[m]!);
  });

  const f = spectra.finish();
  const onsetScale = percentile(onsetRaw, 0.99) || Math.max(...onsetRaw);
  const onset = onsetRaw.map((v) => (onsetScale > 0 ? Math.min(1, v / onsetScale) : 0));

  const beats = tracked.beats.map((b) => Math.round(b * 1000) / 1000);
  const firstTime = onsetFrames[0]!.time;
  const frameOf = (t: number) => Math.round(((t - firstTime) * sampleRate) / HOP_SIZE);
  const downbeats = findDownbeats(beats, env, spectra.bassDb, frameOf, (t0, t1) => chromaOver(f.chroma, frameRate, t0, t1));
  const sections = findSections(beats, downbeats, duration, frameRate, { ...f, onset });

  const bytes = (values: ArrayLike<number>) => {
    const out = new Uint8Array(n);
    for (let i = 0; i < n; i++) out[i] = Math.round(Math.min(1, Math.max(0, values[i]!)) * 255);
    return encodeBase64(out);
  };
  const features: Record<FeatureName, string> = {
    loudness: bytes(f.loudness),
    bass: bytes(f.bass),
    mid: bytes(f.mid),
    treble: bytes(f.treble),
    onset: bytes(onset),
    pitchClass: encodeBase64(f.pitchClass),
    pitchConfidence: bytes(f.pitchConfidence),
    register: bytes(f.register),
  };
  return {
    version: ANALYSIS_VERSION,
    bpm: Math.round(tracked.bpm * 100) / 100,
    duration,
    beats,
    downbeats,
    sections,
    frameRate,
    features,
  };
}

interface FrameFeatures {
  loudness: Float32Array;
  bass: Float32Array;
  mid: Float32Array;
  treble: Float32Array;
  register: Float32Array;
  pitchClass: Uint8Array;
  pitchConfidence: Float32Array;
  // 12 per frame (C first), summing to 1, or all 0 in silence.
  chroma: Float32Array;
  // K-weighted mean square per frame (linear), for section energy.
  power: Float64Array;
}

// Collects per-FFT-frame measurements into output frames (averaging the
// FFT frames whose centers are nearest each output frame).
class SpectrumFeatures {
  // Bass level of every FFT frame (dB), for downbeats.
  readonly bassDb: number[] = [];
  private readonly n: number;
  private readonly frameRate: number;
  private readonly count: Int32Array;
  private readonly loud: Float64Array;
  private readonly bands: Float64Array;
  private readonly centroidSum: Float64Array;
  private readonly centroidWeight: Float64Array;
  private readonly fine: Float32Array;
  private readonly power: Float32Array;
  private readonly kWeight: Float32Array;
  private readonly bandOf: Int8Array;
  private readonly logHz: Float32Array;
  private readonly topBin: number;
  private readonly chromaBins: [number, number];
  private readonly binHz: number;
  // Scales bin power so a frame's total is the signal's mean square.
  private readonly norm: number;

  constructor(sampleRate: number, frameRate: number, n: number) {
    this.n = n;
    this.frameRate = frameRate;
    this.count = new Int32Array(n);
    this.loud = new Float64Array(n);
    this.bands = new Float64Array(3 * n);
    this.centroidSum = new Float64Array(n);
    this.centroidWeight = new Float64Array(n);
    this.fine = new Float32Array(FINE_BINS * n);
    this.binHz = sampleRate / FRAME_SIZE;
    this.topBin = Math.min(FRAME_SIZE / 2 - 1, Math.floor(BANDS_HZ[2]![1] / this.binHz));
    this.power = new Float32Array(this.topBin + 2);
    this.kWeight = new Float32Array(this.topBin + 1);
    this.bandOf = new Int8Array(this.topBin + 1).fill(-1);
    this.logHz = new Float32Array(this.topBin + 1).fill(Number.NaN);
    for (let k = 1; k <= this.topBin; k++) {
      const hz = k * this.binHz;
      this.kWeight[k] = kWeighting(hz);
      this.bandOf[k] = BANDS_HZ.findIndex(([lo, hi]) => hz >= lo && hz < hi);
      if (hz >= REGISTER_HZ[0] && hz < REGISTER_HZ[1]) this.logHz[k] = Math.log2(hz);
    }
    this.chromaBins = [
      Math.max(2, Math.ceil(CHROMA_HZ[0] / this.binHz)),
      Math.min(this.topBin - 1, Math.floor(CHROMA_HZ[1] / this.binHz)),
    ];
    let windowEnergy = 0;
    for (let i = 0; i < FRAME_SIZE; i++) windowEnergy += (0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (FRAME_SIZE - 1))) ** 2;
    this.norm = 2 / (FRAME_SIZE * windowEnergy);
  }

  add(re: Float32Array, im: Float32Array, time: number): void {
    const j = Math.min(this.n - 1, Math.max(0, Math.round(time * this.frameRate)));
    const { power, norm } = this;
    let loud = 0;
    let bass = 0;
    let mid = 0;
    let treble = 0;
    let centroidSum = 0;
    let centroidWeight = 0;
    for (let k = 1; k <= this.topBin; k++) {
      const p = (re[k]! * re[k]! + im[k]! * im[k]!) * norm;
      power[k] = p;
      loud += p * this.kWeight[k]!;
      const band = this.bandOf[k]!;
      if (band === 0) bass += p;
      else if (band === 1) mid += p;
      else if (band === 2) treble += p;
      const logHz = this.logHz[k]!;
      if (logHz === logHz) {
        const m = Math.sqrt(p);
        centroidSum += m * logHz;
        centroidWeight += m;
      }
    }
    this.count[j]!++;
    this.loud[j] = this.loud[j]! + loud;
    this.bands[3 * j] = this.bands[3 * j]! + bass;
    this.bands[3 * j + 1] = this.bands[3 * j + 1]! + mid;
    this.bands[3 * j + 2] = this.bands[3 * j + 2]! + treble;
    this.centroidSum[j] = this.centroidSum[j]! + centroidSum;
    this.centroidWeight[j] = this.centroidWeight[j]! + centroidWeight;
    this.bassDb.push(db(bass));

    // Chroma from spectral peaks. A 2048-point FFT's bins (~22 Hz) are wider
    // than a semitone below ~370 Hz, but a single partial's frequency can be
    // read far more finely from the shape of its peak (parabola through the
    // log power of the peak bin and its neighbors).
    const [lo, hi] = this.chromaBins;
    let max = 0;
    for (let k = lo; k <= hi; k++) max = Math.max(max, power[k]!);
    // Peaks 30 dB under the loudest are left out, including the window's
    // own side lobes (-31 dB).
    const threshold = max * 1e-3;
    if (max <= 0) return;
    const fine = this.fine;
    const base = j * FINE_BINS;
    for (let k = lo; k <= hi; k++) {
      const p = power[k]!;
      if (p < threshold || p <= power[k - 1]! || p < power[k + 1]!) continue;
      const a = Math.log(power[k - 1]! + 1e-30);
      const b = Math.log(p);
      const c = Math.log(power[k + 1]! + 1e-30);
      const curve = a - 2 * b + c;
      const shift = curve < 0 ? Math.max(-0.5, Math.min(0.5, (0.5 * (a - c)) / curve)) : 0;
      const hz = (k + shift) * this.binHz;
      // Fine bins from A: 0 = A, FINE = A#, ...
      let pos = FINE * 12 * Math.log2(hz / 440);
      pos = ((pos % FINE_BINS) + FINE_BINS) % FINE_BINS;
      const low = Math.floor(pos);
      const frac = pos - low;
      const weight = Math.sqrt(p);
      fine[base + (low % FINE_BINS)] = fine[base + (low % FINE_BINS)]! + weight * (1 - frac);
      fine[base + ((low + 1) % FINE_BINS)] = fine[base + ((low + 1) % FINE_BINS)]! + weight * frac;
    }
  }

  finish(): FrameFeatures {
    const { n, count } = this;
    // Averages; frames that no FFT frame landed on copy their neighbor.
    let source = count.findIndex((c) => c > 0);
    for (let j = 0; j < n; j++) {
      if (count[j]! > 0) {
        source = j;
        const c = count[j]!;
        this.loud[j] = this.loud[j]! / c;
        for (let b = 0; b < 3; b++) this.bands[3 * j + b] = this.bands[3 * j + b]! / c;
        for (let b = 0; b < FINE_BINS; b++) this.fine[j * FINE_BINS + b] = this.fine[j * FINE_BINS + b]! / c;
      } else if (source >= 0) {
        this.loud[j] = this.loud[source]!;
        for (let b = 0; b < 3; b++) this.bands[3 * j + b] = this.bands[3 * source + b]!;
        this.fine.copyWithin(j * FINE_BINS, source * FINE_BINS, (source + 1) * FINE_BINS);
        this.centroidSum[j] = this.centroidSum[source]!;
        this.centroidWeight[j] = this.centroidWeight[source]!;
      }
    }

    const loudDb = Float64Array.from(this.loud, db);
    const loudTop = Math.max(percentile(loudDb, 0.99), SILENCE_DB + 10);
    const loudness = loudDb.map((d) => perceived(d - loudTop));
    const sounding = (j: number) => loudDb[j]! > Math.max(loudTop - 50, SILENCE_DB);

    // Each band against its own top, but never more than 30 dB under the
    // strongest band's: a band with nothing in it stays dark instead of
    // having its leakage stretched to full scale.
    const bandDb = [0, 1, 2].map((b) => Float64Array.from({ length: n }, (_, j) => db(this.bands[3 * j + b]!)));
    const tops = bandDb.map((d) => percentile(d, 0.99));
    const strongest = Math.max(...tops);
    const [bass, mid, treble] = bandDb.map((d, b) => {
      const top = Math.max(tops[b]!, strongest - 30, SILENCE_DB + 10);
      return Float32Array.from(d, (v) => perceived(v - top));
    }) as [Float32Array, Float32Array, Float32Array];

    // Register: log centroid stretched over this song's range (at least an
    // octave, so a song in one register doesn't turn its noise into motion).
    const centroid = Float64Array.from({ length: n }, (_, j) =>
      sounding(j) && this.centroidWeight[j]! > 0 ? this.centroidSum[j]! / this.centroidWeight[j]! : Number.NaN,
    );
    const valid = centroid.filter((c) => !Number.isNaN(c));
    let low = percentile(valid, 0.05);
    let high = percentile(valid, 0.95);
    if (high - low < 1) {
      const middle = percentile(valid, 0.5);
      low = middle - 0.5;
      high = middle + 0.5;
    }
    const register = Float32Array.from(centroid, (c) => (Number.isNaN(c) ? 0 : clamp01((c - low) / (high - low))));

    // Tuning: the average offset of all peaks from the nearest semitone (a
    // circular mean over the FINE bins within a semitone).
    let x = 0;
    let y = 0;
    for (let i = 0; i < this.fine.length; i++) {
      const angle = (2 * Math.PI * (i % FINE)) / FINE;
      x += this.fine[i]! * Math.cos(angle);
      y += this.fine[i]! * Math.sin(angle);
    }
    const tuning = Math.round((Math.atan2(y, x) * FINE) / (2 * Math.PI));
    const chroma = new Float32Array(12 * n);
    const pitchClass = new Uint8Array(n).fill(NO_PITCH);
    const pitchConfidence = new Float32Array(n);
    for (let j = 0; j < n; j++) {
      if (!sounding(j)) continue;
      let sum = 0;
      for (let p = 0; p < 12; p++) {
        let v = 0;
        for (let d = -2; d <= 2; d++) {
          const bin = (((FINE * p + tuning + d) % FINE_BINS) + FINE_BINS) % FINE_BINS;
          v += this.fine[j * FINE_BINS + bin]!;
        }
        chroma[12 * j + ((p + 9) % 12)] = v; // A is pitch class 9
        sum += v;
      }
      if (sum <= 0) continue;
      let best = 0;
      for (let p = 0; p < 12; p++) {
        chroma[12 * j + p] = chroma[12 * j + p]! / sum;
        if (chroma[12 * j + p]! > chroma[12 * j + best]!) best = p;
      }
      // 0 when all 12 are equal, 1 when one note has everything.
      const confidence = clamp01((chroma[12 * j + best]! - 1 / 12) / (11 / 12));
      pitchConfidence[j] = confidence;
      if (confidence >= MIN_PITCH_CONFIDENCE) pitchClass[j] = best;
    }
    return {
      loudness: Float32Array.from(loudness),
      bass,
      mid,
      treble,
      register,
      pitchClass,
      pitchConfidence,
      chroma,
      power: this.loud,
    };
  }
}

// Bar phase: of the four ways to group beats into bars of four, the one whose
// first beats have the most bass, the sharpest onsets and the most chord
// changes. Bass and chord changes count most: a backbeat snare (beats 2 and
// 4) often has the sharpest onsets.
function findDownbeats(
  beats: number[],
  env: Float64Array,
  bassDb: number[],
  frameOf: (t: number) => number,
  chromaBetween: (t0: number, t1: number) => Float32Array,
): number[] {
  const k = beats.length;
  const peak = (values: ArrayLike<number>, from: number, to: number) => {
    let best = -Infinity;
    for (let m = Math.max(0, from); m <= Math.min(values.length - 1, to); m++) best = Math.max(best, values[m]!);
    return Number.isFinite(best) ? best : 0;
  };
  const onset = beats.map((t) => peak(env, frameOf(t) - 2, frameOf(t) + 2));
  const bass = beats.map((t) => peak(bassDb, frameOf(t) - 1, frameOf(t) + 3));
  const period = median(beats.slice(1).map((b, i) => b - beats[i]!));
  const chord = beats.map((t) => chromaBetween(t, t + period));
  const change = beats.map((_, i) => (i === 0 ? 0 : 1 - cosine(chord[i - 1]!, chord[i]!)));
  const accent = sum3(zScores(bass), zScores(onset), zScores(change), [1, 0.5, 1]);
  let bestPhase = 0;
  let bestScore = -Infinity;
  for (let phase = 0; phase < Math.min(BEATS_PER_BAR, k); phase++) {
    let total = 0;
    let count = 0;
    for (let i = phase; i < k; i += BEATS_PER_BAR) {
      total += accent[i]!;
      count++;
    }
    if (total / count > bestScore) {
      bestScore = total / count;
      bestPhase = phase;
    }
  }
  return beats.filter((_, i) => i % BEATS_PER_BAR === bestPhase);
}

interface SectionInput {
  loudness: Float32Array;
  bass: Float32Array;
  mid: Float32Array;
  treble: Float32Array;
  onset: Float32Array;
  register: Float32Array;
  chroma: Float32Array;
  power: Float64Array;
}

// Sections (Foote 2000): describe each beat by its chroma and its sound
// (loudness, bands, onsets, register), compare every beat with every other
// (a self-similarity matrix), and slide a checkerboard kernel down the
// diagonal: it scores high where the music before is self-similar, the music
// after is self-similar, and the two differ. Peaks at least MIN_SECTION_S
// apart are boundaries. Sections are then labeled by how similar their
// blocks of the matrix are, so a returning chorus gets the chorus's letter.
function findSections(
  beats: number[],
  downbeats: number[],
  duration: number,
  frameRate: number,
  f: SectionInput,
): AnalysisSection[] {
  // Beat grid over the whole song: the tracked beats, continued at the
  // typical beat length through any quiet intro or outro.
  const period = median(beats.slice(1).map((b, i) => b - beats[i]!));
  const before: number[] = [];
  for (let t = beats[0]! - period; t > period / 4; t -= period) before.unshift(t);
  const after: number[] = [];
  for (let t = beats[beats.length - 1]! + period; t < duration - period / 4; t += period) after.push(t);
  const grid = [0, ...before, ...beats.filter((b) => b > period / 4 && b < duration - period / 4), ...after];
  const segments = grid.length;
  if (duration < 2 * MIN_SECTION_S || segments < 4) return [{ start: 0, label: "A", energy: 1 }];

  // Per-beat vectors: 12 chroma (unit length) then 6 sound features.
  const DIMS = 18;
  const vectors = new Float32Array(segments * DIMS);
  const n = f.loudness.length;
  const sound = [f.loudness, f.bass, f.mid, f.treble, f.onset, f.register];
  for (let s = 0; s < segments; s++) {
    const t0 = grid[s]!;
    const t1 = grid[s + 1] ?? duration;
    const c = chromaOver(f.chroma, frameRate, t0, t1);
    const length = Math.hypot(...c) || 1;
    for (let p = 0; p < 12; p++) vectors[s * DIMS + p] = (c[p]! / length) * Math.SQRT1_2;
    const [j0, j1] = frameRange(frameRate, n, t0, t1);
    sound.forEach((values, d) => {
      let total = 0;
      for (let j = j0; j < j1; j++) total += values[j]!;
      vectors[s * DIMS + 12 + d] = total / (j1 - j0);
    });
  }
  const distance2 = (a: number, b: number) => {
    let sum = 0;
    for (let d = 0; d < DIMS; d++) {
      const diff = vectors[a * DIMS + d]! - vectors[b * DIMS + d]!;
      sum += diff * diff;
    }
    return sum;
  };
  // Similarity scale: the median distance between beats (sampled), with a
  // floor so a song that hardly changes isn't stretched into sections.
  const stride = Math.max(1, Math.floor(segments / 400));
  const sample: number[] = [];
  for (let a = 0; a < segments; a += stride) for (let b = a + stride; b < segments; b += stride) sample.push(distance2(a, b));
  const scale = Math.max(median(sample), 0.02);
  const similarity = (a: number, b: number) => Math.exp(-distance2(a, b) / scale);

  // Novelty at each grid point: within-similarity minus cross-similarity,
  // Gaussian-weighted over half-width w beats either side.
  const w = Math.max(4, Math.min(32, Math.round(MIN_SECTION_S / period)));
  const novelty = new Float64Array(segments);
  for (let i = 1; i < segments; i++) {
    let same = 0;
    let sameWeight = 0;
    let cross = 0;
    let crossWeight = 0;
    for (let a = Math.max(0, i - w); a < Math.min(segments, i + w); a++) {
      for (let b = a + 1; b < Math.min(segments, i + w); b++) {
        const g = Math.exp(-((a - i + 0.5) ** 2 + (b - i + 0.5) ** 2) / (2 * (w / 2) ** 2));
        const s = similarity(a, b);
        if ((a < i) === (b < i)) {
          same += g * s;
          sameWeight += g;
        } else {
          cross += g * s;
          crossWeight += g;
        }
      }
    }
    if (sameWeight > 0 && crossWeight > 0) novelty[i] = same / sameWeight - cross / crossWeight;
  }

  // Boundaries: the strongest local peaks, moved to a downbeat within two
  // beats (sections start on bars), at least MIN_SECTION_S apart and from the ends.
  const snap = (t: number) => {
    const near = downbeats.reduce((best, d) => (Math.abs(d - t) < Math.abs(best - t) ? d : best), Infinity);
    return Math.abs(near - t) <= 2 * period ? near : t;
  };
  const peaks: number[] = [];
  for (let i = 1; i < segments; i++) {
    let isPeak = novelty[i]! >= MIN_NOVELTY;
    for (let j = Math.max(1, i - w); isPeak && j <= Math.min(segments - 1, i + w); j++) {
      if (novelty[j]! > novelty[i]! || (novelty[j] === novelty[i] && j < i)) isPeak = false;
    }
    if (isPeak) peaks.push(i);
  }
  peaks.sort((a, b) => novelty[b]! - novelty[a]!);
  const starts = [0];
  for (const i of peaks) {
    const t = snap(grid[i]!);
    if (t < MIN_SECTION_S || duration - t < MIN_SECTION_S) continue;
    if (starts.every((s) => Math.abs(s - t) >= MIN_SECTION_S)) starts.push(t);
  }
  starts.sort((a, b) => a - b);

  // Grid points of each section, sampled so long songs stay quick.
  const members = starts.map((s, k) => {
    const end = starts[k + 1] ?? Infinity;
    const all = grid.map((_, i) => i).filter((i) => grid[i]! >= s - 1e-6 && grid[i]! < end - 1e-6);
    const step = Math.max(1, Math.floor(all.length / 100));
    return all.filter((_, x) => x % step === 0);
  });
  const blockMean = (a: number[], b: number[]) => {
    let total = 0;
    let count = 0;
    for (const x of a) {
      for (const y of b) {
        if (x === y) continue;
        total += similarity(x, y);
        count++;
      }
    }
    return count > 0 ? total / count : 1;
  };
  const self = members.map((m) => blockMean(m, m));
  const labels: number[] = [];
  members.forEach((m, k) => {
    let bestLabel = -1;
    let best = -Infinity;
    for (let other = 0; other < k; other++) {
      const r = blockMean(m, members[other]!) / Math.sqrt(self[k]! * self[other]!);
      if (r > best) {
        best = r;
        bestLabel = labels[other]!;
      }
    }
    const used = new Set(labels).size;
    labels.push(bestLabel >= 0 && (best >= SAME_LABEL || used >= 26) ? bestLabel : used);
  });

  // Energy: each section's loudness averaged as power (as integrated LUFS
  // is, so drum hits count fully), perceived relative to the loudest section.
  const levels = starts.map((s, k) => {
    const [j0, j1] = frameRange(frameRate, n, s, starts[k + 1] ?? duration);
    let total = 0;
    for (let j = j0; j < j1; j++) total += f.power[j]!;
    return db(total / (j1 - j0));
  });
  const top = Math.max(...levels);
  return starts.map((s, k) => ({
    start: Math.round(s * 1000) / 1000,
    label: String.fromCharCode(65 + labels[k]!),
    energy: Math.round(perceived(levels[k]! - top) * 100) / 100,
  }));
}

// Output frames for [t0, t1): at least one.
function frameRange(frameRate: number, n: number, t0: number, t1: number): [number, number] {
  const j0 = Math.min(n - 1, Math.max(0, Math.ceil(t0 * frameRate - 1e-9)));
  const j1 = Math.min(n, Math.max(j0 + 1, Math.ceil(t1 * frameRate - 1e-9)));
  return [j0, j1];
}

// Mean chroma over [t0, t1).
function chromaOver(chroma: Float32Array, frameRate: number, t0: number, t1: number): Float32Array {
  const [j0, j1] = frameRange(frameRate, chroma.length / 12, t0, t1);
  const out = new Float32Array(12);
  for (let j = j0; j < j1; j++) for (let p = 0; p < 12; p++) out[p] = out[p]! + chroma[12 * j + p]!;
  return out;
}

// K-weighting (ITU BS.1770, the LUFS curve) as a power gain: a high-pass
// near 38 Hz and +4 dB above ~1.5 kHz. Unlike A-weighting it keeps a kick
// drum's weight, which matters at music levels.
function kWeighting(hz: number): number {
  const f2 = hz * hz;
  const highPass = (f2 * f2) / (f2 * f2 + 38 ** 4);
  const shelf = 1 + (10 ** 0.4 - 1) * (f2 / (f2 + 1500 ** 2));
  return highPass * shelf;
}

function db(power: number): number {
  return 10 * Math.log10(power + 1e-20);
}

// Perceived loudness relative to a reference (doubles every 10 dB), with
// -50 dB as 0.
function perceived(relativeDb: number): number {
  return clamp01((2 ** (relativeDb / 10) - FLOOR) / (1 - FLOOR));
}

function percentile(values: ArrayLike<number>, q: number): number {
  if (values.length === 0) return 0;
  const sorted = Float64Array.from(values).sort();
  return sorted[Math.floor(q * (sorted.length - 1))]!;
}

function median(values: number[]): number {
  return percentile(values, 0.5);
}

function zScores(values: number[]): number[] {
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const std = Math.sqrt(values.reduce((a, b) => a + (b - mean) ** 2, 0) / values.length);
  return values.map((v) => (std > 1e-9 ? (v - mean) / std : 0));
}

function sum3(a: number[], b: number[], c: number[], weights: [number, number, number]): number[] {
  return a.map((v, i) => weights[0] * v + weights[1] * b[i]! + weights[2] * c[i]!);
}

function cosine(a: Float32Array, b: Float32Array): number {
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i]! * b[i]!;
    na += a[i]! * a[i]!;
    nb += b[i]! * b[i]!;
  }
  return na > 0 && nb > 0 ? dot / Math.sqrt(na * nb) : 1;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
